import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { surveyService } from '../../../services/surveyService';
import api from '../../../services/api';
import {
  Button, StatusBadge, Card, Modal, Spinner,
  EmptyState, PageHeader, Input, Select, PeriodBadges
} from '../../../shared/sharedComponent';
import { toast } from 'react-toastify';
import '../SurveyList/SurveyList.css';

const STATUS_OPTIONS = ['', 'submitted', 'overdue', 'closed'];
const STATUS_LABELS = { overdue: 'Pending', submitted: 'Completed' };
const TYPE_OPTIONS   = ['', 'monthly', 'quarterly'];
const SECTOR_OPTIONS = ['', 'manufacturing', 'wholesale_retail'];
const FORM_OPTIONS   = ['', 'MQ1', 'WRTQ1', 'MM1', 'Monthly_WRT', 'GQ1'];
const FORM_LABELS = {
  MQ1: 'MQ1 — Manufacturing Quarterly',
  WRTQ1: 'WRTQ1 — Wholesale & Retail Quarterly',
  MM1: 'MM1 — Manufacturing Monthly',
  Monthly_WRT: 'Monthly WRT — Wholesale & Retail Monthly',
  GQ1: 'GQ1 — Generic Questionnaire',
};

const timeAgo = (dateStr) => {
  if (!dateStr) return null;
  const diff   = Date.now() - new Date(dateStr).getTime();
  const mins   = Math.floor(diff / 60000);
  const hours  = Math.floor(diff / 3600000);
  const days   = Math.floor(diff / 86400000);
  const weeks  = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7)   return `${days}d ago`;
  if (weeks < 5)  return `${weeks}w ago`;
  return `${months}mo ago`;
};

const statusTime = (s) => {
  if (s.survey_status === 'submitted') return timeAgo(s.survey_date_submitted);
  if (s.survey_status === 'closed')    return timeAgo(s.survey_close_date);
  if (s.survey_status === 'draft')     return timeAgo(s.created_at);
  return timeAgo(s.survey_close_date);
};

const PastSurveys = () => {
  const navigate = useNavigate();

  const [surveys, setSurveys]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [filters, setFilters]     = useState({ status: '', survey_type: '', sector: '', form_code: '', date_from: '', date_to: '' });

  // Dispatch log modal
  const [logModal, setLogModal]     = useState(false);
  const [logSurvey, setLogSurvey]   = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const setFilter = (key, val) => {
    setFilters((prev) => ({ ...prev, [key]: val }));
    setPage(1);
  };

  const clearDates = () => setFilters((prev) => ({ ...prev, date_from: '', date_to: '' }));

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await surveyService.getAll({ ...filters, page, limit: 15, view: 'past' });
      setSurveys(data.data || []);
      setPagination({
        total: data.pagination?.total || 0,
        pages: data.pagination?.pages || 1,
      });
    } catch { toast.error('Failed to load surveys.'); }
    finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { loadSurveys(); }, [loadSurveys]);

  const openDispatchLog = async (survey) => {
    setLogSurvey(survey);
    setLogModal(true);
    setLogLoading(true);
    try {
      const { data } = await api.get(`/surveys/${survey.survey_id}/dispatch-log`);
      setLogEntries(data.data || []);
    } catch { toast.error('Failed to load dispatch log.'); }
    finally { setLogLoading(false); }
  };

  return (
    <div className="surveys-list">
      <PageHeader
        title="Past Surveys"
        subtitle="Surveys that have passed their due date"
        actions={
          <Button variant="secondary" onClick={() => navigate('/internal/surveys')}>
            ← Active Surveys
          </Button>
        }
      />

      {/* ── Filters ── */}
      <Card className="surveys-list__filters">
        <div className="filters-row">
          <div className="surveys-filter__dates">
            <label className="surveys-filter__dates-label">Date</label>
            <div className="surveys-filter__dates-row">
              <span className="surveys-filter__dates-field">
                <span className="surveys-filter__dates-prefix">From</span>
                <input type="date" value={filters.date_from} max={filters.date_to || undefined}
                  onChange={(e) => setFilter('date_from', e.target.value)} />
              </span>
              <span className="surveys-filter__dates-sep">|</span>
              <span className="surveys-filter__dates-field">
                <span className="surveys-filter__dates-prefix">To</span>
                <input type="date" value={filters.date_to} min={filters.date_from || undefined}
                  onChange={(e) => setFilter('date_to', e.target.value)} />
              </span>
              {(filters.date_from || filters.date_to) && (
                <button className="surveys-filter__clear-dates" onClick={clearDates}>✕</button>
              )}
            </div>
          </div>
          <Select className="surveys-filter__select" value={filters.status} onChange={(e) => setFilter('status', e.target.value)} label="Status">
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s ? (STATUS_LABELS[s] || s.replace('_', ' ').replace(/^\w/, (c) => c.toUpperCase())) : 'All statuses'}</option>
            ))}
          </Select>
          <Select className="surveys-filter__select" value={filters.survey_type} onChange={(e) => setFilter('survey_type', e.target.value)} label="Type">
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t ? t.charAt(0).toUpperCase() + t.slice(1) : 'All types'}</option>
            ))}
          </Select>
          <Select className="surveys-filter__select" value={filters.sector} onChange={(e) => setFilter('sector', e.target.value)} label="Industry">
            {SECTOR_OPTIONS.map((s) => (
              <option key={s} value={s}>{s ? s.replace('_', ' & ').replace(/^\w/, (c) => c.toUpperCase()) : 'All Industries'}</option>
            ))}
          </Select>
          <Select className="surveys-filter__select" value={filters.form_code} onChange={(e) => setFilter('form_code', e.target.value)} label="Form">
            {FORM_OPTIONS.map((f) => (
              <option key={f} value={f}>{f ? FORM_LABELS[f] : 'All forms'}</option>
            ))}
          </Select>
        </div>
      </Card>

      {/* ── Table ── */}
      <Card padding={false}>
        <div className="surveys-table">
          <div className="surveys-table__head">
            <span></span>
            <span>Survey</span>
            <span>Data Source</span>
            <span>Period</span>
            <span>Dates</span>
            <span>Status / Dispatched</span>
            <span>Actions</span>
          </div>

          {loading ? (
            <div className="surveys-list__loading"><Spinner size="lg" /></div>
          ) : surveys.length === 0 ? (
            <EmptyState
              title="No past surveys"
              description="Surveys that have passed their due date will appear here."
            />
          ) : surveys.map((s) => (
            <div key={s.survey_id} className="surveys-table__row">
              {/* Checkbox placeholder */}
              <div className="surveys-table__col surveys-table__col--check" />

              {/* Survey */}
              <div className="surveys-table__col">
                <span className="surveys-table__code">{s.survey_form_code}</span>
                {s.is_universal && (
                  <span className="surveys-table__universal-badge">🌐 Universal</span>
                )}
                {s.deadline_extended && (
                  <span className="surveys-table__extended-badge">⏱ Deadline Extended</span>
                )}
                <span className="surveys-table__title">{s.survey_title}</span>
                <span className="surveys-table__creator">by {s.created_by_name}</span>
              </div>

              {/* Establishment */}
              <div className="surveys-table__col">
                {s.is_universal
                  ? <span className="surveys-table__all-badge">All</span>
                  : <span className="surveys-table__est">{s.establishment_name || '—'}</span>
                }
              </div>

              {/* Period */}
              <div className="surveys-table__col">
                <PeriodBadges periods={s.period_names?.length ? s.period_names : [s.quarter_name || s.month_name]} />
              </div>

              {/* Dates */}
              <div className="surveys-table__col surveys-table__col--dates">
                <span><strong>Open:</strong> {new Date(s.survey_open_date).toLocaleDateString('en-NA')}</span>
                <span><strong>Close:</strong> {new Date(s.survey_close_date).toLocaleDateString('en-NA')}</span>
              </div>

              {/* Status pill */}
              <div className="surveys-table__col surveys-table__col--meta">
                <div className="surveys-table__meta-pill">
                  <StatusBadge status={s.survey_status} />
                  <span className="surveys-table__meta-divider" />
                  {statusTime(s) && (
                    <span className="surveys-table__meta-ago">{statusTime(s)}</span>
                  )}
                  {statusTime(s) && <span className="surveys-table__meta-divider" />}
                  <span className="surveys-table__meta-count">{s.dispatched_count ?? 0}</span>
                </div>
              </div>

              {/* Actions — view + emails only (no dispatch for past) */}
              <div className="surveys-table__col surveys-table__col--actions">
                <div className="sl-actions">
                  <button className="sl-action sl-action--view"
                    onClick={() => navigate(`/internal/surveys/${s.survey_id}`)}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
                    View
                  </button>
                  {s.dispatched_count > 0 && (
                    <button className="sl-action sl-action--emails"
                      onClick={() => openDispatchLog(s)}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>
                      Emails
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="surveys-list__pagination">
            <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</Button>
            <span>Page {page} of {pagination.pages}</span>
            <Button variant="secondary" size="sm" disabled={page === pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
          </div>
        )}
      </Card>

      {/* ── Dispatch Log Modal ── */}
      <Modal
        open={logModal}
        onClose={() => setLogModal(false)}
        title={`Email Dispatch Log — ${logSurvey?.survey_form_code}`}
        size="md"
      >
        <div className="dispatch-log">
          <p className="dispatch-log__subtitle">{logSurvey?.survey_title}</p>
          {logLoading ? (
            <div style={{ textAlign: 'center', padding: '32px' }}><Spinner /></div>
          ) : logEntries.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '24px', color: 'var(--gray-400)' }}>
              No emails dispatched for this survey.
            </p>
          ) : (
            <div className="dispatch-log__list">
              <div className="dispatch-log__stats">
                <div className="dispatch-log__stat dispatch-log__stat--sent">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                  <div>
                    <span className="dispatch-log__stat-num">{logEntries.filter(e => e.email_sent).length}</span>
                    <span className="dispatch-log__stat-label">Sent</span>
                  </div>
                </div>
                <div className="dispatch-log__stat dispatch-log__stat--failed">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-9a1 1 0 012 0v4a1 1 0 01-2 0V9zm1 7a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
                  <div>
                    <span className="dispatch-log__stat-num">{logEntries.filter(e => !e.email_sent).length}</span>
                    <span className="dispatch-log__stat-label">Failed</span>
                  </div>
                </div>
              </div>
              {logEntries.map((entry) => (
                <div key={entry.dispatch_id} className="dispatch-log__row">
                  <div className="dispatch-log__row-left">
                    <div className={`dispatch-log__avatar ${!entry.email_sent ? 'dispatch-log__avatar--failed' : ''}`}>
                      {entry.dp_name?.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                    </div>
                    <div className="dispatch-log__info">
                      <span className="dispatch-log__name">{entry.dp_name}</span>
                      <span className="dispatch-log__email">{entry.dp_email}</span>
                      <span className="dispatch-log__date">Sent: {new Date(entry.dispatched_at).toLocaleString('en-NA')}</span>
                    </div>
                  </div>
                  {entry.email_sent ? (
                    <span className="dispatch-log__badge dispatch-log__badge--sent">
                      <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M13.707 4.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L6 10.586l6.293-6.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      Email sent
                    </span>
                  ) : (
                    <span className="dispatch-log__badge dispatch-log__badge--failed">
                      <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M8 1a7 7 0 100 14A7 7 0 008 1zM7 5a1 1 0 012 0v3a1 1 0 01-2 0V5zm1 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
                      Failed
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PastSurveys;